import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authRedirectService } from '../../../../services/authRedirectService';
import { meetingSessionService } from '../../../../services/meetingSessionService';
import type { UseJitsiMeetingProps } from '../../../../types/useJitsiMeeting.types';
import { initializeJitsiAPI } from '../../../../utils/jitsiAPIUtils';
import { isDev } from '../../../../utils/optimizedImports';

export const useJitsiMeeting = ({
  activeToken,
  meetingId,
  meetingDisplayName,
  isMeetingDisplayNamePending,
  isAuthenticated,
  apiRef,
  setIsMeetingVisible,
  setIsChatOpen,
  removeOverlay,
  onJitsiError,
  clearJitsiErrors,
  onConferenceJoined,
  onLobbyEntered,
  onMeetingLeaving,
  extraParams,
  startDeviceSync,
  cleanup,
  setupPasswordListener,
  cancelPendingRef,
  guestName,
  displayName,
}: UseJitsiMeetingProps) => {
  const navigate = useNavigate();
  // Internal ref to track meeting setup state
  const isMeetingSetUp = useRef(false);

  const removeSessionForMeetingLink = useCallback(() => {
    authRedirectService.removeMeetingHashParams();
  }, []);

  // Store callbacks in refs to prevent unnecessary re-renders
  const callbacksRef = useRef({
    setIsMeetingVisible,
    setIsChatOpen,
    removeOverlay,
    onJitsiError,
    clearJitsiErrors,
    onConferenceJoined,
    onLobbyEntered,
    onMeetingLeaving,
    startDeviceSync,
    cleanup,
    setupPasswordListener,
    navigate,
    removeSessionForMeetingLink,
    isMeetingSetUp,
    apiRef,
    cancelPendingRef,
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      setIsMeetingVisible,
      setIsChatOpen,
      removeOverlay,
      onJitsiError,
      clearJitsiErrors,
      onConferenceJoined,
      onLobbyEntered,
      onMeetingLeaving,
      startDeviceSync,
      cleanup,
      setupPasswordListener,
      navigate,
      removeSessionForMeetingLink,
      isMeetingSetUp,
      apiRef,
      cancelPendingRef,
    };
  }, [
    setIsMeetingVisible,
    setIsChatOpen,
    removeOverlay,
    onJitsiError,
    clearJitsiErrors,
    onConferenceJoined,
    onLobbyEntered,
    onMeetingLeaving,
    startDeviceSync,
    cleanup,
    setupPasswordListener,
    navigate,
    removeSessionForMeetingLink,
    apiRef,
    cancelPendingRef,
  ]);

  // Store current token and auth state in refs for use in effects
  // This prevents the API initialization from re-running when these values change
  const activeTokenRef = useRef(activeToken);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const extraParamsRef = useRef(extraParams);
  const displayNameRef = useRef(displayName ?? guestName);
  const meetingDisplayNameRef = useRef(meetingDisplayName);

  useEffect(() => {
    activeTokenRef.current = activeToken;
    isAuthenticatedRef.current = isAuthenticated;
    extraParamsRef.current = extraParams;
    displayNameRef.current = displayName ?? guestName;
    meetingDisplayNameRef.current = meetingDisplayName;
  }, [activeToken, isAuthenticated, extraParams, displayName, guestName, meetingDisplayName]);

  // Ref for the meeting we currently own the API for (used to decide dispose only when session changes).
  const meetingSessionRef = useRef<string | null>(null);
  const isUnmountedRef = useRef(false);

  // Single effect: set up or dispose Jitsi API based on meetingSession (===), token, and mount state.
  useEffect(() => {
    if (isDev) {
      console.log('activeToken updated:', activeToken ? 'PRESENT' : 'NULL');
    }

    let isMounted = true;

    // Reset isMeetingSetUp if API was disposed externally (e.g. handleTOSCancel).
    if (!apiRef.current && isMeetingSetUp.current) {
      if (isDev) {
        console.log('[Cleanup] API was disposed externally, resetting isMeetingSetUp');
      }
      isMeetingSetUp.current = false;
    }

    // Session changed: dispose previous API if it belonged to a different meeting.
    if (apiRef.current && meetingSessionRef.current !== meetingId) {
      try {
        console.info('[Jitsi] API object is being disposed (meeting session changed).');
        apiRef.current.dispose();
      } catch (e) {
        console.warn('[Cleanup] Error disposing Jitsi API on meeting session change:', e);
      }
      apiRef.current = null;
      isMeetingSetUp.current = false;
      meetingSessionService.setMeetingActive(false);
    }
    meetingSessionRef.current = meetingId;

    // Initialize when we have meetingId + token and no API for this session.
    // Jitsi reads the display name only when the iframe is created, so wait for the lookup to finish.
    if (
      meetingId &&
      activeToken &&
      !isMeetingDisplayNamePending &&
      !isMeetingSetUp.current &&
      !apiRef.current
    ) {
      if (isDev) {
        console.log('Setting up meeting...');
      }
      isMeetingSetUp.current = true;
      meetingSessionService.setMeetingActive(true);
      console.info('[Jitsi] API object is being set up/created.');

      initializeJitsiAPI({
        meetingId,
        meetingDisplayName: meetingDisplayNameRef.current,
        activeToken,
        isAuthenticated: isAuthenticatedRef.current,
        extraParams: extraParamsRef.current,
        callbacks: callbacksRef.current,
        displayName: displayNameRef.current,
      })
        .then((api) => {
          if (!isMounted) {
            try {
              api.dispose();
            } catch (e) {
              console.warn('[Cleanup] Error disposing Jitsi API:', e);
            }
            return;
          }
        })
        .catch((error) => {
          if (!isMounted) {
            return;
          }
          console.error('[Jitsi] Failed to initialize Jitsi API:', error);
          if (isDev) {
            console.error('[Jitsi] Error details:', error);
          }
          isMeetingSetUp.current = false;
          meetingSessionService.setMeetingActive(false);
          callbacksRef.current.setIsMeetingVisible(false);
        });
    } else if (!activeToken && isMeetingSetUp.current) {
      const meetingContainer = document.querySelector('#meeting-container') as HTMLElement;
      if (meetingContainer) {
        meetingContainer.classList.remove('visible');
        meetingContainer.style.display = 'none';
      }
      document.body.classList.remove('meeting-active');
      callbacksRef.current.setIsMeetingVisible(false);
      callbacksRef.current.setIsChatOpen(false);
      callbacksRef.current.cleanup();
      isMeetingSetUp.current = false;
      meetingSessionService.setMeetingActive(false);
    }

    return () => {
      isMounted = false;

      // Always dispose on unmount so no stale iframe/instance remains (fast rejoin).
      // Session changes are handled at the start of the next effect run (meetingSessionRef.current !== meetingId).
      if (apiRef.current && isUnmountedRef.current) {
        try {
          console.info('[Jitsi] API object is being disposed (unmount).');
          apiRef.current.dispose();
        } catch (e) {
          console.warn('[Cleanup] Error disposing Jitsi API:', e);
        }
        apiRef.current = null;
        isMeetingSetUp.current = false;
        meetingSessionService.setMeetingActive(false);
      }
    };
  }, [meetingId, activeToken, isMeetingDisplayNamePending, apiRef]);

  useEffect(() => {
    if (!meetingId) {
      return;
    }

    const defaultTitle = document.title || 'Sangoma Meet';
    document.title = `${meetingId} | ${defaultTitle}`;

    return () => {
      document.title = defaultTitle;
    };
  }, [meetingId]);

  // Declared after main effect so its cleanup runs first on unmount; main effect cleanup then sees isUnmountedRef.current === true.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Release pending-init resources (timers, online/offline listeners) on real
  // unmount only — `cancelPendingRef` has stable identity so cleanup never re-fires.
  useEffect(() => () => cancelPendingRef.current?.(), [cancelPendingRef]);
};
